"use client";

import type { Message } from "@ai-sdk/react";
import { captureClientEvent } from "@/lib/telemetry-client";
import { createContext, useContext, useEffect, useState } from "react";
import { useChat } from "@ai-sdk/react";

interface ChatContextType {
  messages: Message[];
  setMessages: (messages: Message[]) => void;
  input: string;
  isLoading: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleSubmit: (e?: React.FormEvent) => void;
  setInput: (input: string) => void;
  sessionId: string | undefined;
  setSessionId: (id: string) => void;
  initialMessages: Message[];
  setInitialMessages: (messages: Message[]) => void;
  hasChatConsent: boolean;
  setHasChatConsent: (consent: boolean) => void;
  error: Error | undefined;
  reload: () => Promise<string | null | undefined>;
}

const ChatContext = createContext<ChatContextType | null>(null);

export function ChatProvider({
  children,
  sessionId: initialSessionId,
  hasChatConsent: initialHasChatConsent,
  welcomeMessage,
}: {
  children: React.ReactNode;
  sessionId: string | undefined;
  hasChatConsent: boolean;
  welcomeMessage: Message;
}) {
  const [sessionId, setSessionId] = useState<string | undefined>(
    initialSessionId,
  );
  const [initialMessages, setInitialMessages] = useState<Message[]>([]);
  const [hasChatConsent, setHasChatConsent] = useState<boolean>(
    initialHasChatConsent,
  );

  useEffect(() => {
    if (initialSessionId !== sessionId) {
      setSessionId(initialSessionId);
    }
  }, [initialSessionId]);

  const chat = useChat({
    api: "/api/chat",
    body: { sessionId },
    initialMessages: [welcomeMessage, ...initialMessages],
    async onFinish(response: any) {
      const content =
        response.content !== ""
          ? response.content
          : response.toolInvocations?.[0]?.result?.output;
      captureClientEvent("chat_response_received", {
        has_content: Boolean(content),
        has_tool_invocations: Boolean(response.toolInvocations?.length),
      });
    },

    async onError(error: any) {
      console.error("Error in chat:", error);
      captureClientEvent("chat_response_failed", {
        error: error instanceof Error ? error.message : String(error),
      });
      // setError("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
    },
  });

  return (
    <ChatContext.Provider
      value={{
        ...chat,
        sessionId,
        setSessionId,
        initialMessages,
        setInitialMessages,
        hasChatConsent,
        setHasChatConsent,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useGlobalChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useGlobalChat must be used within a ChatProvider");
  }
  return context;
}
